import PropTypes from 'prop-types';
import React from 'react';

import { useTranslation } from 'react-i18next';
import moment from 'moment-timezone';

import { Grid, Dropdown, Icon } from 'semantic-ui-react';
import { Table, TableBody, TableHeader, TableRow, TableHeaderCell, TableCell, Status } from '@weezevent/nacre';

import Styles from '../../styles/rate/rateCreation.module.css';

export const StatusEvent = React.memo(({ status, removed = null }) => {
    const { t } = useTranslation();

    const color = React.useMemo(() => {
        if (removed) {
            return 'red';
        }

        if (status === 'closed') {
            return 'grey';
        }

        return status === 'draft' ? 'orange' : 'green';
    }, [status]);

    return <Status text={t(`ticket.rate_template.${removed ? 'deleted' : status}`)} color={color} />;
});

function LinkedEventRow({ organizationId, event, events = [] }) {
    const { t } = useTranslation();

    let link = `/ticket/O${organizationId}/E${event.id}/settings/rates`;
    // If there's only one event (thus rate) generated from this template
    // we can redirect the user right to the rate page instead of the list
    if (event.rates.length === 1) {
        link += `/${event.rates[0].id}`;
    }

    let { start_date, timezone, publication_status, removed } = events.find(e => e.id === event.id) || {};

    return (
        <TableRow key={event.id}>
            <TableCell>{event.name}</TableCell>
            <TableCell>{event.id}</TableCell>
            <TableCell>{new Date(start_date).toLocaleDateString([], { day: '2-digit', month: '2-digit', year: 'numeric', timeZone: timezone })}</TableCell>
            <TableCell style={{ textAlign: 'center' }} className={'events-picker-status'}>
                <StatusEvent status={publication_status} removed={removed} />
            </TableCell>
            <TableCell style={{ textAlign: 'center' }}>
                {t('ticket.rate_template.table_to_link_events_already_linked')} <Icon className="linkify" />
            </TableCell>
            <TableCell style={{ textAlign: 'center' }}>
                <a target={'_blank'} rel="noopener noreferrer" className="form-link-event" href={link}>
                    <Icon className="external alternate" />
                </a>
            </TableCell>
        </TableRow>
    );
}

LinkedEventRow.propTypes = {
    organizationId: PropTypes.number.isRequired,
    event: PropTypes.object.isRequired,
    events: PropTypes.array.isRequired
};

export default function EventsPicker({ organizationId, events = [], selected = [], onSave = () => {}, rate, formatLabel }) {
    const { t } = useTranslation();

    const eventsPrefiltered = React.useMemo(() => {
        return events.filter(event => {
            if (event.removed) {
                return false;
            }

            if (event.publication_status === 'closed') {
                return false;
            }

            if (event.end_date && event.end_date < moment.utc().format()) {
                return false;
            }
            return !rate?.events?.map(event => event.id).includes(event.id);
        });
    }, [events, rate]);

    const [selectedEvents, setSelectedEvents] = React.useState(selected);
    const [filteredEvents, setFilteredEvents] = React.useState(eventsPrefiltered);
    const [querySearch, setQuerySearch] = React.useState('');

    const selectEvent = React.useCallback(
        eventId => {
            let newSelectedEvents = [eventsPrefiltered.find(event => eventId === event.id), ...selectedEvents];
            setSelectedEvents(newSelectedEvents);
        },
        [selectedEvents, eventsPrefiltered]
    );

    const deselectEvent = React.useCallback(
        event => {
            let newSelectedEvents = selectedEvents.filter(selectedEvent => event.id !== selectedEvent.id);
            setSelectedEvents(newSelectedEvents);
        },
        [selectedEvents]
    );

    const generateOptions = filtered => {
        return filtered.map(item => ({
            text: (
                <div className={'events-picker-options'}>
                    <div>
                        <div className={'events-picker-options-name'}>{item.name}</div>
                        <div className={'events-picker-options-date'}>
                            {new Date(item.start_date).toLocaleDateString([], {
                                day: '2-digit',
                                month: '2-digit',
                                year: 'numeric',
                                timeZone: item.timezone
                            }) +
                                ' - ' +
                                item.id}
                        </div>
                    </div>
                    <div>
                        <StatusEvent status={item.publication_status} />
                    </div>
                </div>
            ),
            value: item.pk,
            key: item.pk
        }));
    };

    const filterEvents = React.useCallback(() => {
        let words = querySearch.split(' ');

        let filtered = filteredEvents.filter(event => {
            let regexpWords = words.map(word => new RegExp(word, 'gi'));
            return regexpWords.reduce((acc, regexp) => acc + (regexp.test(`${event.id} ${event.name}`) ? 1 : 0), 0) === regexpWords.length;
        });

        return generateOptions(filtered);
    }, [filteredEvents, querySearch]);

    React.useEffect(() => {
        setFilteredEvents(eventsPrefiltered.filter(event => !selectedEvents.map(event => event.id).includes(event.id)));
        onSave(selectedEvents);
    }, [events, eventsPrefiltered, selectedEvents]);

    return (
        <div>
            <Grid>
                <Grid.Column width={16}>
                    <Dropdown
                        className={'eventsPicker'}
                        closeOnChange={true} /* for close after click because props multiple */
                        floating={true}
                        fluid
                        multiple /* multiple is used here to not have a default value */
                        noResultsMessage={t('ticket.rate_template.events_picker_dropdown_no_results')}
                        onChange={(synth, data) => selectEvent(data.value[0])}
                        onSearchChange={(e, { searchQuery }) => setQuerySearch(searchQuery)}
                        options={generateOptions(filteredEvents)}
                        placeholder={t('ticket.rate_template.events_picker_dropdown_placeholder')}
                        search={filterEvents}
                        searchQuery={querySearch}
                        selection
                        selectOnBlur={false}
                        value={[]}
                    ></Dropdown>
                    <div className={'events-picker-count-events'}>
                        {t('ticket.rate_template.events_picker_count_events_to_link', { count: selectedEvents.length })}
                        {', '}
                        {t('ticket.rate_template.events_picker_count_linked_events', { count: rate?.events?.length || 0 })}
                    </div>
                </Grid.Column>
            </Grid>
            {/*<div className="modal-container" />*/}
            {(rate?.events?.length > 0 || selectedEvents.length > 0) && (
                <div className={Styles['table-settings-events-container']}>
                    <Table basic celled responsive={false}>
                        <TableHeader>
                            <TableRow>
                                <TableHeaderCell>{t('ticket.rate_template.table_to_link_events_name')}</TableHeaderCell>
                                <TableHeaderCell>{t('ticket.rate_template.table_to_link_events_id')}</TableHeaderCell>
                                <TableHeaderCell>{t('ticket.rate_template.table_to_link_events_date')}</TableHeaderCell>
                                <TableHeaderCell style={{ textAlign: 'center' }}>{t('ticket.rate_template.table_to_link_events_event_status')}</TableHeaderCell>
                                <TableHeaderCell style={{ textAlign: 'center' }}>{t('ticket.rate_template.table_to_link_events_rate_status')}</TableHeaderCell>
                                <TableHeaderCell style={{ width: '100px' }}></TableHeaderCell>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {selectedEvents.map(event => (
                                <TableRow key={event.id}>
                                    <TableCell>{event.name}</TableCell>
                                    <TableCell>{event.id}</TableCell>
                                    <TableCell>
                                        {new Date(event.start_date).toLocaleDateString([], { day: '2-digit', month: '2-digit', year: 'numeric', timeZone: event.timezone })}
                                    </TableCell>
                                    <TableCell style={{ textAlign: 'center' }} className={'events-picker-status'}>
                                        <StatusEvent status={event.publication_status} />
                                    </TableCell>
                                    <TableCell style={{ textAlign: 'center' }}>-</TableCell>
                                    <TableCell style={{ textAlign: 'center' }}>
                                        <button className="form-remove-event" onClick={() => deselectEvent(event)}>
                                            {t('ticket.common.action.remove')}
                                        </button>
                                    </TableCell>
                                </TableRow>
                            ))}
                            {rate?.events?.map(event => (
                                <LinkedEventRow key={event.id} events={events} organizationId={organizationId} event={event} />
                            ))}
                        </TableBody>
                    </Table>
                </div>
            )}
        </div>
    );
}

EventsPicker.propDefaults = {
    rate: null
};

EventsPicker.propTypes = {
    onSave: PropTypes.func.isRequired,
    events: PropTypes.array.isRequired,
    selected: PropTypes.array.isRequired,
    organizationId: PropTypes.number.isRequired,
    formatLabel: PropTypes.func.isRequired,
    rate: PropTypes.object
};
